// The fulfill flows' inbound picker rows — the `linkableDeclarationsForOrder` connection.
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { buildQuantitiesByUnit, defaultUnitType } from '@/lib/mappers/quantities';
import { formatDateOrDash, formatTextOrDash, formatPercentOrDash } from '@/lib/format';
import { EMPTY_LIST } from '@/lib/utils';
import {
  feedstockCategoryLabel,
  allocationPreferenceLabel,
  toAllocationPreferenceArray,
} from '@/config/formOptions';

const SUGGESTION_GROUP = 'SUGGESTION';

// An inbound reports what is left, so what is already committed is the difference.
const allocatedFromAvailable = (total, available) =>
  total == null || available == null ? null : total - available;

// A Declaration node names its quantities differently from the REST inbound DTO.
const buildLinkableDeclarationByUnit = (node) =>
  buildQuantitiesByUnit({
    [UNIT_TYPES.working]: {
      unit: node.workingUnit,
      amounts: {
        balanceQuantity: node.availableQuantityWorking,
        allocatedQuantity: allocatedFromAvailable(
          node.adjustedQuantityWorking,
          node.availableQuantityWorking,
        ),
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: node.declarationUnit,
      amounts: {
        balanceQuantity: node.availableQuantityDeclaration,
        allocatedQuantity: allocatedFromAvailable(
          node.adjustedQuantityDeclaration,
          node.availableQuantityDeclaration,
        ),
      },
    },
    [UNIT_TYPES.tracking]: {
      unit: node.trackingUnit,
      amounts: {
        balanceQuantity: node.availableQuantityTracking,
        allocatedQuantity: allocatedFromAvailable(
          node.adjustedQuantityTracking,
          node.availableQuantityTracking,
        ),
      },
    },
  });

// One row of Fulfill ▸ Select inbound — a `LinkableOrderDeclaration`.
export const processLinkableOrderDeclaration = (entry) => {
  const node = entry.declaration;
  const byUnit = buildLinkableDeclarationByUnit(node);
  return {
    id: node.id,
    declarationNo: formatTextOrDash(node.declarationNo),
    referenceId: formatTextOrDash(node.referenceId),
    byUnit,
    unitType: defaultUnitType(byUnit),
    // The certified cell's ceiling: the row's own balance, raw, per unit.
    unassignedByUnit: {
      [UNIT_TYPES.working]: node.availableQuantityWorking ?? null,
      [UNIT_TYPES.declaration]: node.availableQuantityDeclaration ?? null,
      [UNIT_TYPES.tracking]: node.availableQuantityTracking ?? null,
    },
    ghgEmissions: formatTextOrDash(node.ghgEmissions),
    ghgEmissionSavings: formatPercentOrDash(node.ghgEmissionSavings),
    // The footer's weighted average needs the number, not the formatted string.
    ghgEmissionSavingsRaw: node.ghgEmissionSavings ?? null,
    rawMaterialType: formatTextOrDash(node.rawMaterialType),
    countryOfOrigin: formatTextOrDash(node.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(node.feedstockCategory)),
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(node.allocationPreference)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    dateOfDispatch: formatDateOrDash(node.dateOfDispatch),
    dateOfDispatchRaw: node.dateOfDispatch ?? null,
    dateOfDischarge: formatDateOrDash(node.dateOfDischarge),
    discrepancyCount: node.discrepancyCount ?? 0,
    isSuggestion: entry.group === SUGGESTION_GROUP,
    // Why the row is an "other" — the REST route never sent these.
    sustainabilityMismatches: entry.sustainabilityMismatches ?? EMPTY_LIST,
  };
};

/**
 * `suggestions` is complete and unpaginated, `nodes` is the paginated rest, so the two
 * concatenate into the one ranked list the picker draws — suggestions first, as before.
 */
export const processLinkableDeclarationsForOrder = (connection) => ({
  rows: [...(connection?.suggestions ?? []), ...(connection?.nodes ?? [])].map(
    processLinkableOrderDeclaration,
  ),
  total: (connection?.suggestions?.length ?? 0) + (connection?.pageInfo?.total ?? 0),
  hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
});

/**
 * The allocations a fulfil just created. The strategies answer in three shapes: the created
 * transaction rows, one row, or v1's `{ requests: [{ transactionDbId }] }` — an allocation's id
 * is its transaction id in all three.
 */
export const processFulfilledAllocationIds = (result) => {
  const ids = Array.isArray(result)
    ? result.map((row) => row?.id)
    : Array.isArray(result?.requests)
      ? result.requests.map((request) => request?.transactionDbId)
      : [result?.id];
  return ids.filter((id) => id != null).map(String);
};
